"use client";

import React, { type ReactNode } from "react";
import * as Atoms from "@package/ui";

import type { UiNode } from "../index-types";
import type { AtomContext, RenderLocals, SubmittedFormValues } from "./registry-types";
import {
  extractTriggerName,
  mergeFormValues,
  normalizeAriaHiddenProp,
  resolveRenderableValue,
  isPlainObject,
  invokeTrigger,
} from "./render-ui-node.utils";
import {
  bindBooleanControl,
  bindChipGroupControl,
  bindTextControl,
} from "./render-ui-node.bindings";

const BOOLEAN_ATOM_TYPES = new Set(["Switch", "Checkbox", "Radio", "Toggle"]);
const BOOLEAN_INPUT_TYPES = new Set(["checkbox", "radio", "switch"]);
const TEXT_LIKE_AS_VALUES = new Set(["input", "textarea", "select"]);

function renderChildren(children: UiNode[] | undefined, ctx: AtomContext, locals: RenderLocals): ReactNode[] {
  return (children ?? []).map((child, index) => (
    <React.Fragment key={index}>{renderRenderableNode(child, ctx, locals)}</React.Fragment>
  ));
}

function stripInternalProps(props: Record<string, unknown>, options?: { keepBindName?: boolean }): Record<string, unknown> {
  const {
    onClickTrigger: _onClickTrigger,
    onSubmitTrigger: _onSubmitTrigger,
    onChangeTrigger: _onChangeTrigger,
    onCheckedChangeTrigger: _onCheckedChangeTrigger,
    onValueChange: _onValueChange,
    onChange: _onChange,
    onCheckedChange: _onCheckedChange,
    eventPayload: _eventPayload,
    bindName: _bindName,
    external: _external,
    ...cleanProps
  } = props;

  const next = options?.keepBindName
    ? { ...cleanProps, ...(props.bindName !== undefined ? { bindName: props.bindName } : {}) }
    : cleanProps;

  return next;
}

function renderForEach(value: unknown, ctx: AtomContext, locals: RenderLocals): ReactNode {
  if (!isPlainObject(value)) return null;
  const itemsValue = resolveRenderableValue(value.items, ctx, locals);
  if (!Array.isArray(itemsValue)) return null;
  const itemAs = typeof value.itemAs === "string" && value.itemAs.trim() ? value.itemAs.trim() : "item";
  return itemsValue.map((item, index) => (
    <React.Fragment key={index}>{renderRenderableNode(value.template, ctx, { ...locals, [itemAs]: item, index })}</React.Fragment>
  ));
}

function renderRenderableNode(spec: unknown, ctx: AtomContext, locals: RenderLocals): ReactNode {
  if (spec === null || spec === undefined || spec === false) return null;
  if (typeof spec === "string") {
    const resolved = resolveRenderableValue(spec, ctx, locals);
    return resolved !== spec ? renderRenderableNode(resolved, ctx, locals) : spec;
  }
  if (typeof spec === "number") return spec;
  if (Array.isArray(spec)) {
    return spec.map((item, index) => <React.Fragment key={index}>{renderRenderableNode(item, ctx, locals)}</React.Fragment>);
  }
  if (!isPlainObject(spec)) return null;
  if (Object.prototype.hasOwnProperty.call(spec, "$if") && !Boolean(resolveRenderableValue(spec.$if, ctx, locals))) return null;
  if (Object.prototype.hasOwnProperty.call(spec, "$forEach")) return renderForEach(spec.$forEach, ctx, locals);
  if (Object.prototype.hasOwnProperty.call(spec, "$bind") && !Object.prototype.hasOwnProperty.call(spec, "type")) return null;
  return renderUiNodeNode(spec as unknown as UiNode, ctx, locals);
}

function isTextLikeControl(nodeType: string, props: Record<string, unknown>): boolean {
  if (["Input", "Select", "Textarea"].includes(nodeType)) return true;
  return nodeType === "Box" && TEXT_LIKE_AS_VALUES.has(String(props.as ?? "").toLowerCase());
}

function isInputLikeNode(nodeType: string, props: Record<string, unknown>): boolean {
  if (isTextLikeControl(nodeType, props)) return true;
  return String(props.as ?? "").toLowerCase() === "input";
}

function isBooleanControlNode(nodeType: string, props: Record<string, unknown>): boolean {
  if (BOOLEAN_ATOM_TYPES.has(nodeType)) return true;
  const inputType = String(props.type ?? "").toLowerCase();
  if (inputType !== "" && BOOLEAN_INPUT_TYPES.has(inputType)) return true;
  return props.checked !== undefined && isInputLikeNode(nodeType, props);
}

function readNodeTextValue(node: UiNode): unknown {
  if (
    Object.prototype.hasOwnProperty.call(node, "text") &&
    node.text !== null &&
    node.text !== undefined
  ) {
    return node.text;
  }

  const props = node.props;
  if (
    isPlainObject(props) &&
    Object.prototype.hasOwnProperty.call(props, "text") &&
    props.text !== null &&
    props.text !== undefined
  ) {
    return props.text;
  }

  return null;
}

function renderNodeText(node: UiNode, ctx: AtomContext, locals: RenderLocals): ReactNode {
  const rawText = readNodeTextValue(node);
  if (rawText === null) return null;

  const resolved = resolveRenderableValue(rawText, ctx, locals);
  if (typeof resolved === "string") {
    return resolved.length > 0 ? resolved : null;
  }
  if (typeof resolved === "number" || typeof resolved === "boolean") {
    return String(resolved);
  }
  return null;
}

function extractSubmittedFormValues(first: unknown): SubmittedFormValues | undefined {
  if (!first || typeof first !== "object") return undefined;

  const record = first as { values?: unknown; currentTarget?: unknown };

  if (isPlainObject(record.values)) {
    return record.values as SubmittedFormValues;
  }

  if (
    typeof FormData === "function" &&
    typeof HTMLFormElement !== "undefined" &&
    record.currentTarget instanceof HTMLFormElement
  ) {
    const entries = new FormData(record.currentTarget).entries();
    return Object.fromEntries(entries) as SubmittedFormValues;
  }

  return undefined;
}

function preventDefaultOnNativeSubmit(first: unknown): void {
  if (!first || typeof first !== "object") return;
  const candidate = first as { preventDefault?: unknown };
  if (typeof candidate.preventDefault === "function") {
    candidate.preventDefault();
  }
}

const LOCALE_SEGMENT_PATTERN = /^[a-z]{2}(?:-[A-Za-z]{2})?$/;
const URL_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+.-]*:/;
const LOCALE_PREFIXED_HREF = /^\/([a-z]{2}(?:-[A-Za-z]{2})?)(\/|$)/i;
const EMBEDDED_ABSOLUTE_URL = /(?:^|\/)([a-zA-Z][a-zA-Z\d+.-]*:\/\/[^\s]*)/;

function hasUrlScheme(value: string): boolean {
  return value.startsWith("//") || URL_SCHEME_PATTERN.test(value);
}

/**
 * Peel stacked locale prefixes (and a stray leading slash) until a bare
 * absolute URL remains. Returns the original string when the href is an
 * ordinary path.
 *
 * `/en/https://doi.org/...`, `/en/en/https://...`, and `/https://...` must
 * classify as absolute on both SSR and the client. Prefixing those URLs
 * produces a different anchor branch (external `<a>` vs NextLink) and
 * fails hydration.
 */
function unwrapLocalePrefixedAbsoluteHref(target: string): string {
  const trimmed = target.trim();
  if (!trimmed) return trimmed;

  let current = trimmed;
  for (let depth = 0; depth < 8; depth += 1) {
    if (hasUrlScheme(current)) return current;
    if (current.startsWith("/") && hasUrlScheme(current.slice(1))) {
      return current.slice(1);
    }

    const match = LOCALE_PREFIXED_HREF.exec(current);
    if (!match) break;

    // Keep the slash that separated `/en` from the remainder so
    // `/en/https://...` becomes `/https://...` rather than `https://...`
    // only after the scheme check above, and `/en/en/https://...` can
    // peel another locale segment.
    current = current.slice(match[1].length + 1);
  }

  if (hasUrlScheme(current)) return current;
  if (current.startsWith("/") && hasUrlScheme(current.slice(1))) {
    return current.slice(1);
  }
  return trimmed;
}

/**
 * Return a bare absolute URL when `value` is (or hides) one.
 *
 * Absolute hrefs must never be locale-prefixed. This extracts
 * `https://doi.org/...` from `https://...`, `/https://...`, and
 * `/en/https://...` so later path localization cannot run.
 */
function extractBareAbsoluteHref(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const peeled = unwrapLocalePrefixedAbsoluteHref(trimmed);
  for (const candidate of [peeled, trimmed]) {
    if (hasUrlScheme(candidate)) return candidate;
    if (candidate.startsWith("/") && hasUrlScheme(candidate.slice(1))) {
      return candidate.slice(1);
    }
  }

  const embedded = EMBEDDED_ABSOLUTE_URL.exec(peeled) ?? EMBEDDED_ABSOLUTE_URL.exec(trimmed);
  if (embedded?.[1]) return embedded[1];

  const fromColon = peeled.includes("://") ? peeled : trimmed.includes("://") ? trimmed : "";
  if (fromColon) {
    const schemeIndex = fromColon.search(/[a-zA-Z][a-zA-Z\d+.-]*:\/\//);
    if (schemeIndex >= 0) return fromColon.slice(schemeIndex);
  }

  return null;
}

/**
 * Normalize a node's raw href BEFORE it reaches any downstream component.
 *
 * INVARIANT: an absolute URL is NEVER locale-prefixed and never carries a
 * leading-slash-wrapped scheme. Any href that is (or hides) an absolute URL
 * is reduced to its bare absolute form here, identically on the server and
 * during client hydration. Button/Link and the renderer's own anchor wrap
 * therefore always observe the same string, the Link atom classifies the
 * href as external on BOTH renders, and both emit the identical plain
 * `<a href="https://..." target rel>` — no NextLink branch, no hydration
 * mismatch, and external links (DOI chips, sources) stay absolute without
 * locale prefixes.
 */
function normalizeNodeHref(target: string): string {
  const raw = target.trim();
  if (!raw) return raw;
  return extractBareAbsoluteHref(raw) ?? raw;
}

/**
 * Resolve hrefs from data available on both the server and the first client
 * render.
 *
 * INVARIANT: an absolute URL is NEVER locale-prefixed. Before any locale
 * handling runs, extract a bare absolute URL when one is present and return
 * it unchanged. The locale-prefix branch can therefore only ever see
 * path-like hrefs (no scheme anywhere), so the Link atom classifies absolute
 * URLs as external on both renders and hydration cannot diverge between the
 * external plain <a> and NextLink.
 */
function buildCrawlableHref(target: string, ctx: AtomContext): string {
  const raw = normalizeNodeHref(target);
  if (!raw) return raw;

  // Absolute URLs stay absolute and unprefixed on every render. Do this
  // before adding a leading slash or a locale prefix — those steps are what
  // previously turned `https://doi.org/...` into `/en/https://doi.org/...`.
  const absoluteHref = extractBareAbsoluteHref(raw);
  if (absoluteHref) return absoluteHref;

  if (raw.startsWith("#") || raw.startsWith("?")) return raw;

  const unwrapped = unwrapLocalePrefixedAbsoluteHref(raw);
  const path = unwrapped.startsWith("/") ? unwrapped : `/${unwrapped}`;
  const absoluteFromPath = extractBareAbsoluteHref(path);
  if (absoluteFromPath) return absoluteFromPath;

  const urlLocale = typeof ctx.urlLocale === "string" ? ctx.urlLocale.trim() : "";
  if (urlLocale && LOCALE_SEGMENT_PATTERN.test(urlLocale)) {
    const prefix = `/${urlLocale}`;
    if (path === prefix || path.startsWith(`${prefix}/`)) return path;

    const localized = `/${urlLocale}${path === "/" ? "" : path}`;
    // INVARIANT: never return a scheme-bearing href behind a locale prefix.
    // If localization accidentally produced one, peel it back to the bare
    // absolute URL instead of returning the prefixed form.
    return extractBareAbsoluteHref(localized) ?? localized;
  }
  return path;
}

function isOrdinaryClick(event: React.MouseEvent<HTMLElement>): boolean {
  return !event.defaultPrevented && event.button === 0 &&
    !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function renderUiNodeNode(node: UiNode, ctx: AtomContext, locals: RenderLocals): ReactNode {
  const Component = (Atoms as unknown as Record<string, React.ElementType>)[node.type];
  if (!Component) return null;

  const hasPropsLevelText =
    isPlainObject(node.props) && Object.prototype.hasOwnProperty.call(node.props, "text");

  const normalizedProps = normalizeAriaHiddenProp(resolveRenderableValue(node.props || {}, ctx, locals) as Record<string, unknown>);
  const onClickTriggerName = extractTriggerName(normalizedProps.onClickTrigger);
  const onSubmitTriggerName = extractTriggerName(normalizedProps.onSubmitTrigger);
  const onCheckedChangeTriggerName = extractTriggerName(normalizedProps.onCheckedChangeTrigger ?? normalizedProps.onCheckedChange);
  const onChangeTriggerName = extractTriggerName(normalizedProps.onChangeTrigger);
  const eventPayload = normalizedProps.eventPayload;
  const fieldName = typeof normalizedProps.name === "string" && normalizedProps.name.trim()
    ? normalizedProps.name.trim()
    : typeof normalizedProps.bindName === "string" && normalizedProps.bindName.trim()
      ? normalizedProps.bindName.trim()
      : undefined;
  // `external` is a renderer-level directive, never a DOM attribute. Forwarding
  // it onto Button/Link props leaked `external={true}` onto the rendered
  // <a> (React non-boolean attribute warning). Absolute hrefs are classified
  // as external by the Link atom itself, so the directive is simply stripped
  // here like the other internal props.
  const props = stripInternalProps(normalizedProps, {
    keepBindName: node.type === "Chip" && Boolean(fieldName),
  });

  if (hasPropsLevelText) {
    delete props.text;
  }

  // Button and Link already render anchors for hrefs. Do not wrap their
  // interactive content in a second anchor containing a button.
  // The raw href is normalized FIRST (bare absolute URL extracted before
  // anything else) so Button/Link receive the identical, unprefixed string
  // on the server and the client — absolute URLs can never be re-prefixed
  // downstream.
  const rawHrefRaw = typeof props.href === "string" ? props.href.trim() : "";
  const rawHref = rawHrefRaw ? normalizeNodeHref(rawHrefRaw) : "";
  const directLink = Boolean(rawHref) && (node.type === "Button" || node.type === "Link");
  delete props.href;
  if (directLink) props.href = buildCrawlableHref(rawHref, ctx);

  const currentValue = ctx.getFormValues()[fieldName ?? ""];
  const isBooleanControl = isBooleanControlNode(node.type, props);

  if (node.type === "Icon" && typeof props.fill === "boolean") {
    delete props.fill;
  }

  const isNativeInputNode = String(props.as ?? "").toLowerCase() === "input";
  const nativeInputType = String(props.type ?? "").toLowerCase();
  const isNativeBooleanInput =
    isNativeInputNode &&
    (BOOLEAN_INPUT_TYPES.has(nativeInputType) || props.checked !== undefined);

  if (node.type === "Chip") {
    bindChipGroupControl(props, fieldName, currentValue, ctx.setFormValue, { triggerName: onChangeTriggerName, ctx, locals, eventPayload });
  } else if (isBooleanControl) {
    bindBooleanControl(props, fieldName, currentValue, ctx.setFormValue, { triggerName: onCheckedChangeTriggerName ?? onChangeTriggerName ?? onClickTriggerName, ctx, locals, eventPayload, allowOnCheckedChange: !isNativeBooleanInput });
  } else if (isTextLikeControl(node.type, normalizedProps) && (fieldName || onChangeTriggerName)) {
    bindTextControl(props, fieldName, currentValue, ctx.setFormValue, {
      triggerName: onChangeTriggerName,
      ctx,
      locals,
      eventPayload,
      emitValueChange: node.type === "Select" || String(props.as ?? "").toLowerCase() === "select",
    });
  }

  if (onClickTriggerName && !props.onClick) {
    props.onClick = (event: React.MouseEvent<HTMLElement>) => {
      if (rawHref) {
        // Let modified clicks use the real href (including its locale prefix).
        if (!isOrdinaryClick(event)) return;
        if (directLink) event.preventDefault();
      }
      invokeTrigger(ctx, onClickTriggerName, locals, eventPayload);
    };
  }

  if (onSubmitTriggerName) {
    props.onSubmit = (...args: unknown[]) => {
      const first = args[0];
      preventDefaultOnNativeSubmit(first);
      const submitted = extractSubmittedFormValues(first);
      invokeTrigger(ctx, onSubmitTriggerName, locals, eventPayload, mergeFormValues(ctx.getFormValues(), submitted));
    };
  }

  if (
    props.checked !== undefined &&
    props.onChange === undefined &&
    props.onCheckedChange === undefined &&
    !props.readOnly
  ) {
    props.defaultChecked = Boolean(props.checked);
    delete props.checked;
  }

  if (
    isInputLikeNode(node.type, props) &&
    props.onChange !== undefined &&
    props.value === undefined &&
    props.checked === undefined &&
    props.defaultValue === undefined
  ) {
    props.defaultValue = "";
  }

  const children = renderChildren(node.children, ctx, locals);
  const textNode = renderNodeText(node, ctx, locals);
  if (textNode !== null) children.push(textNode);

  const element = <Component {...props}>{children.length > 0 ? children : undefined}</Component>;

  if (!rawHref || directLink) return element;

  // Other, non-link atoms retain the renderer-level href directive.
  const anchorProps: Record<string, unknown> = {
    href: buildCrawlableHref(rawHref, ctx),
  };
  if (props.onClick !== undefined) {
    anchorProps.onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isOrdinaryClick(event)) event.preventDefault();
    };
  }
  return <a {...anchorProps}>{element}</a>;
}

export function renderUiNode(node: UiNode, ctx: AtomContext): ReactNode {
  return renderRenderableNode(node, ctx, {});
}